import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';

export interface ManagerDeclineDialogData {
  employeeName: string;
}

export interface ManagerDeclineDialogResult {
  confirmed: boolean;
  reason: string;
}

@Component({
  selector: 'ers-manager-decline-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, MatDialogModule],
  template: `
    <div class="mdd-overlay">
      <button class="mdd-close" mat-dialog-close>
        <span class="material-icons">close</span>
      </button>

      <h2 class="mdd-title">Confirm Decline</h2>

      <div class="mdd-employee">
        <div class="mdd-avatar">{{ initials }}</div>
        <span class="mdd-name">{{ data.employeeName }}</span>
      </div>

      <label class="mdd-label">Write a reason for rejection <span class="mdd-required">*</span></label>
      <textarea
        class="mdd-textarea"
        [(ngModel)]="reason"
        rows="4"
        placeholder="Enter reason..."
      ></textarea>

      <div class="mdd-actions">
        <button class="mdd-btn mdd-btn--cancel" (click)="onNo()">No</button>
        <button class="mdd-btn mdd-btn--confirm" [disabled]="!reason.trim()" (click)="onConfirm()">
          Yes, Decline Request
        </button>
      </div>
    </div>
  `,
  styles: [`
    .mdd-overlay {
      position: relative;
      padding: 28px 28px 24px;
      background: #fff;
      border-radius: 12px;
      font-family: inherit;
    }
    .mdd-close {
      position: absolute;
      top: 12px;
      right: 12px;
      background: #D01A2C;
      border: none;
      border-radius: 50%;
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      color: #fff;
      .material-icons { font-size: 14px; line-height: 1; }
    }
    .mdd-title {
      font-size: 16px;
      font-weight: 700;
      color: #1e293b;
      margin: 0 0 20px;
    }
    .mdd-employee {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-bottom: 20px;
    }
    .mdd-avatar {
      width: 36px;
      height: 36px;
      border-radius: 50%;
      background: #2469A0;
      color: #fff;
      font-size: 12px;
      font-weight: 700;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }
    .mdd-name {
      font-size: 14px;
      font-weight: 600;
      color: #1e293b;
    }
    .mdd-label {
      display: block;
      font-size: 13px;
      font-weight: 500;
      color: #1e293b;
      margin-bottom: 8px;
    }
    .mdd-required {
      color: #D01A2C;
    }
    .mdd-textarea {
      width: 100%;
      border: 1px solid #E2E8F0;
      border-radius: 8px;
      padding: 10px 12px;
      font-size: 13px;
      color: #1e293b;
      resize: none;
      outline: none;
      box-sizing: border-box;
      font-family: inherit;
      &:focus { border-color: #2469A0; }
    }
    .mdd-actions {
      display: flex;
      justify-content: flex-end;
      gap: 10px;
      margin-top: 20px;
    }
    .mdd-btn {
      padding: 8px 20px;
      border-radius: 6px;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      border: none;
      &:disabled { opacity: 0.5; cursor: not-allowed; }
    }
    .mdd-btn--cancel {
      background: #fff;
      color: #1e293b;
      border: 1px solid #E2E8F0;
    }
    .mdd-btn--confirm {
      background: #D01A2C;
      color: #fff;
    }
  `],
})
export class ManagerDeclineDialogComponent {
  reason = '';
  initials = '';

  constructor(
    public dialogRef: MatDialogRef<ManagerDeclineDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ManagerDeclineDialogData,
  ) {
    this.initials = (data.employeeName ?? '')
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((w: string) => w[0]?.toUpperCase() ?? '')
      .join('');
  }

  onNo(): void {
    this.dialogRef.close({ confirmed: false, reason: '' });
  }

  onConfirm(): void {
    if (!this.reason.trim()) return;
    this.dialogRef.close({ confirmed: true, reason: this.reason.trim() });
  }
}
